// TV1-1782 ticket 24 — Isomorphic `can()` evaluator.
//
// Moved from `src/apps/web-app/src/hooks/useCapability.ts` so both the
// frontend ( React hooks ) and the backend ( e.g. invariant helpers,
// scripts ) can import the SAME implementation. Predicates from
// `Resources` are the single source of truth — never re-implement scope
// semantics in consumers.
//
// Isomorphism contract ( SDD §7.1 ): for the same ( user, key, instance )
// triple the answer MUST match the backend's `scopeInvariant` /
// `FilterByScopeTransformer` decision.
import { Capabilities } from './registry';
import type { CapabilityKey } from './registry';
import { Resources } from './resources';
import type { Actor, ScopeLevel } from './types';

/**
 * Minimal user shape consumed by `can()`. The FE projects this from the
 * authenticated profile; the BE can pass any object that satisfies the
 * fields. The shape avoids any framework / context coupling so the
 * function stays isomorphic.
 *
 * The `userId` field is read by `Resources` predicates that anchor scopes
 * on the actor's id. FE callers translate from their `User.id` at the
 * boundary.
 */
export type CanUser = {
  userId: string;
  assignedScopes?: string[];
  /**
   * `string[]`, not `ScopeLevel[]`, on purpose: this map arrives over the wire
   * (the generated `/users-profiles/me` type declares
   * `{ [key: string]: (string)[] }`, and the edge-minted authorization token
   * carries arbitrary JSON), so a literal-union declaration here would be
   * stricter than reality and force a cast at every call site. The narrowing
   * happens once, safely, at the predicate lookup below — an unrecognised scope
   * yields `undefined` and is skipped rather than granting anything.
   */
  scopesByCapability?: Record<string, string[]>;
};

const toActor = ( user: CanUser ): Actor => ({
  userId: user.userId,
  assignedScopes: user.assignedScopes ?? []
});

/**
 * Isomorphic capability check. Returns `true` iff the user is authenticated,
 * has at least one scope granted for `key`, AND ( either holds the `all`
 * scope, or — when `instance` is provided — at least one of the held scopes'
 * predicates returns true for that instance ).
 *
 * When `instance` is omitted the function returns `true` as soon as the user
 * has ANY scope for the capability — this is the typical nav-gating case
 * ( "show the menu item if the user can do this for at least some instance" ).
 * Per-instance gating must always pass `instance`.
 */
export function can(
  user: CanUser | null | undefined,
  key: CapabilityKey,
  instance?: Record<string, unknown>
): boolean {
  if ( !user ) return false;
  const scopes = user.scopesByCapability?.[ key ] ?? [];
  if ( scopes.length === 0 ) return false;
  if ( scopes.includes( 'all' )) return true;
  if ( instance === undefined ) return true;

  const capability = Capabilities[ key ];
  if ( !capability ) return false;
  const resource = Resources[ capability.resource ];
  if ( !resource ) return false;

  const actor = toActor( user );
  for ( const scope of scopes ) {
    const predicate = resource.predicates[ scope as ScopeLevel ];
    if ( predicate && predicate( actor, instance )) return true;
  }
  return false;
}
